// Typed fetch client — all API calls go through this module.
// Better Auth session cookies are sent automatically (credentials: 'include').

const BASE = '/api'
const WORKSPACE_STORAGE_KEY = 'openclaw.activeWorkspaceId'
const REQUESTED_WORKSPACE_DENIED = 'Requested workspace is not accessible'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export function getActiveWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(WORKSPACE_STORAGE_KEY)
}

export function setActiveWorkspaceId(workspaceId: string | null): void {
  if (typeof window === 'undefined') return
  if (!workspaceId) {
    localStorage.removeItem(WORKSPACE_STORAGE_KEY)
    return
  }
  localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId)
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  settings: { workspaceScoped?: boolean } = {},
): Promise<T> {
  const workspaceScoped = settings.workspaceScoped ?? true
  const workspaceId = workspaceScoped ? getActiveWorkspaceId() : null

  const headers = new Headers(options.headers)
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (workspaceScoped && workspaceId) headers.set('X-Workspace-Id', workspaceId)

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include', // Send Better Auth session cookie
    headers,
  })

  const activeWorkspace = res.headers.get('x-active-workspace-id')
  if (activeWorkspace) {
    setActiveWorkspaceId(activeWorkspace)
  }

  if (res.status === 204) return undefined as T

  if (!res.ok) {
    const body = await res.json().catch(() => null)

    // Auto-heal stale workspace headers (e.g. after user/account resets) and retry once.
    const workspaceDenied =
      (body as any)?.error === REQUESTED_WORKSPACE_DENIED
      || (body as any)?.message === REQUESTED_WORKSPACE_DENIED
    if (workspaceScoped && workspaceId && res.status === 403 && workspaceDenied) {
      setActiveWorkspaceId(null)
      return request<T>(path, options, settings)
    }

    throw new ApiError(res.status, (body as any)?.error ?? (body as any)?.message ?? res.statusText, body)
  }

  return res.json() as Promise<T>
}

export const api = {
  // ── Agents ───────────────────────────────────────────────────
  agents: {
    list: () => request<Agent[]>('/agents'),
    get: (id: string) => request<Agent>(`/agents/${id}`),
    create: (data: CreateAgentInput) =>
      request<Agent>('/agents', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreateAgentInput>) =>
      request<Agent>(`/agents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    updateStatus: (id: string, status: AgentStatus) =>
      request<Agent>(`/agents/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    delete: (id: string) =>
      request<void>(`/agents/${id}`, { method: 'DELETE' }),
  },

  // ── AI Providers ──────────────────────────────────────────────
  providers: {
    list: () => request<AIConfig[]>('/providers'),
    create: (data: CreateAIConfigInput) =>
      request<AIConfig>('/providers', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreateAIConfigInput>) =>
      request<AIConfig>(`/providers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) =>
      request<void>(`/providers/${id}`, { method: 'DELETE' }),
  },

  // ── Usage ─────────────────────────────────────────────────────
  usage: {
    summary: (days = 30) => request<UsageSummary>(`/usage/summary?days=${days}`),
  },

  // ── Organization (active workspace details) ──────────────────
  org: {
    get: () => request<Org>('/org'),
    create: (data: { name: string; sector?: string }) =>
      request<Org>('/org', { method: 'POST', body: JSON.stringify(data) }, { workspaceScoped: false }),
    update: (data: {
      name?: string
      sector?: string
      logoUrl?: string
      customDomain?: string
      domainStatus?: DomainStatus
      themeConfig?: Record<string, unknown>
    }) =>
      request<Org>('/org', { method: 'PATCH', body: JSON.stringify(data) }),
  },

  // ── Workspaces ────────────────────────────────────────────────
  workspaces: {
    list: async () => {
      const res = await request<WorkspaceListResponse>('/workspaces', {}, { workspaceScoped: false })
      if (res.activeWorkspaceId) setActiveWorkspaceId(res.activeWorkspaceId)
      return res
    },
    create: (data: CreateWorkspaceInput) =>
      request<Workspace>('/workspaces', { method: 'POST', body: JSON.stringify(data) }, { workspaceScoped: false }),
    switch: async (workspaceId: string) => {
      const response = await request<{ workspaceId: string; switched: boolean }>(
        '/workspaces/switch',
        { method: 'POST', body: JSON.stringify({ workspaceId }) },
        { workspaceScoped: false },
      )
      setActiveWorkspaceId(workspaceId)
      return response
    },
    update: (workspaceId: string, data: UpdateWorkspaceInput) =>
      request<Workspace>(`/workspaces/${workspaceId}`, { method: 'PATCH', body: JSON.stringify(data) }, { workspaceScoped: false }),
  },

  // ── Onboarding contact preferences ────────────────────────────
  onboarding: {
    contact: {
      get: () =>
        request<OnboardingContact>('/onboarding/contact', {}, { workspaceScoped: false }),
      save: (data: { email?: string; newsletterOptIn?: boolean }) =>
        request<OnboardingContact>(
          '/onboarding/contact',
          { method: 'POST', body: JSON.stringify(data) },
          { workspaceScoped: false },
        ),
    },
  },

  // ── Shared Ops ────────────────────────────────────────────────
  ops: {
    secrets: {
      list: () => request<OpsSecret[]>('/ops/secrets'),
      create: (data: CreateOpsSecretInput) =>
        request<OpsSecret>('/ops/secrets', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: UpdateOpsSecretInput) =>
        request<OpsSecret>(`/ops/secrets/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      delete: (id: string) => request<void>(`/ops/secrets/${id}`, { method: 'DELETE' }),
    },
    hostingConnections: {
      list: () => request<HostingConnection[]>('/ops/hosting-connections'),
      create: (data: CreateHostingConnectionInput) =>
        request<HostingConnection>('/ops/hosting-connections', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: UpdateHostingConnectionInput) =>
        request<HostingConnection>(`/ops/hosting-connections/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      sync: (id: string) =>
        request<HostingConnection>(`/ops/hosting-connections/${id}/sync`, { method: 'POST' }),
    },
    channelProfiles: {
      list: () => request<ChannelProfile[]>('/ops/channel-profiles'),
      create: (data: CreateChannelProfileInput) =>
        request<ChannelProfile>('/ops/channel-profiles', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: UpdateChannelProfileInput) =>
        request<ChannelProfile>(`/ops/channel-profiles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      delete: (id: string) =>
        request<void>(`/ops/channel-profiles/${id}`, { method: 'DELETE' }),
    },
  },

  // ── Channels ──────────────────────────────────────────────────
  channels: {
    profiles: {
      list: () => request<Array<Pick<ChannelProfile, 'id' | 'name' | 'channelType' | 'config' | 'isActive' | 'createdAt' | 'updatedAt'>>>('/channels/profiles'),
    },
    endpoints: {
      list: () => request<ChannelEndpoint[]>('/channels/endpoints'),
      create: (data: CreateChannelEndpointInput) =>
        request<ChannelEndpoint>('/channels/endpoints', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: UpdateChannelEndpointInput) =>
        request<ChannelEndpoint>(`/channels/endpoints/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    },
    bindings: {
      list: (agentId?: string) =>
        request<AgentChannelBinding[]>(`/channels/bindings${agentId ? `?agentId=${encodeURIComponent(agentId)}` : ''}`),
      create: (data: CreateAgentChannelBindingInput) =>
        request<AgentChannelBinding>('/channels/bindings', { method: 'POST', body: JSON.stringify(data) }),
      delete: (id: string) =>
        request<void>(`/channels/bindings/${id}`, { method: 'DELETE' }),
    },
  },

  // ── Curated offerings ────────────────────────────────────────
  offeringPacks: {
    list: (params?: { audience?: string; vertical?: string }) => {
      const query = new URLSearchParams()
      if (params?.audience) query.set('audience', params.audience)
      if (params?.vertical) query.set('vertical', params.vertical)
      const suffix = query.toString() ? `?${query.toString()}` : ''
      return request<OfferingPack[]>(`/offering-packs${suffix}`)
    },
    create: (data: CreateOfferingPackInput) =>
      request<OfferingPack>('/offering-packs', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CreateOfferingPackInput>) =>
      request<OfferingPack>(`/offering-packs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    apply: (data: { agentId: string; offeringPackId: string }) =>
      request<Agent>('/offering-packs/apply', { method: 'POST', body: JSON.stringify(data) }),
  },

  // ── Billing ───────────────────────────────────────────────────
  billing: {
    invoices: {
      list: () => request<InvoicePeriod[]>('/billing/invoices'),
      get: (id: string) => request<InvoicePeriod>(`/billing/invoices/${id}`),
      generate: (data: GenerateInvoiceInput) =>
        request<InvoicePeriod>('/billing/invoices/generate', { method: 'POST', body: JSON.stringify(data) }),
    },
  },

  // ── Chat (streaming) ─────────────────────────────────────────
  chat: {
    /**
     * Returns a Response with a streaming SSE body.
     * Use readSSEStream() to consume it.
     */
    stream: (agentId: string, message: string, history: ChatMessage[]) => {
      const workspaceId = getActiveWorkspaceId()
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (workspaceId) headers['X-Workspace-Id'] = workspaceId

      return fetch(`${BASE}/chat/${agentId}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ message, conversationHistory: history }),
      })
    },
  },
}

// ── Shared types ──────────────────────────────────────────────────────────────

export type AgentStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED'
export type WorkspaceType = 'SHARED_OPS' | 'FRANCHISE'
export type DomainStatus = 'UNCONFIGURED' | 'PENDING_VERIFICATION' | 'VERIFIED' | 'FAILED'
export type WorkspaceRole =
  | 'OPS_OWNER'
  | 'OPS_OPERATOR'
  | 'FRANCHISE_ADMIN'
  | 'FRANCHISE_MEMBER'
  | 'ADMIN'
  | 'MEMBER'
export type ChannelType = 'TELEGRAM' | 'WHATSAPP' | 'PHONE' | 'WEB'
export type ConnectorType = 'RAILWAY'
export type ConnectorStatus = 'DRAFT' | 'ACTIVE' | 'ERROR' | 'DISABLED'

export interface Agent {
  id: string
  name: string
  slug: string
  description: string | null
  systemPrompt: string | null
  status: AgentStatus
  metadata: Record<string, unknown>
  orgId: string
  aiConfigId: string | null
  aiConfig: AIConfig | null
  createdById: string
  createdBy: { id: string; name: string } | null
  createdAt: string
  updatedAt: string
}

export interface CreateAgentInput {
  name: string
  description?: string
  systemPrompt?: string
  aiConfigId?: string
  metadata?: Record<string, unknown>
}

export interface AIConfig {
  id: string
  name: string
  providerId: string
  modelId: string
  baseUrl: string | null
  settings: Record<string, unknown>
  isDefault: boolean
  isActive: boolean
  hasApiKey: boolean
  createdAt: string
}

export interface CreateAIConfigInput {
  name: string
  providerId: string
  modelId: string
  apiKey?: string
  baseUrl?: string
  settings?: Record<string, unknown>
  isDefault?: boolean
}

export interface UsageSummary {
  totalTokens: number
  totalCost: string
  requestCount: number
  byProvider: Record<string, { tokens: number; cost: number; count: number }>
  byChannel: Record<string, { tokens: number; cost: number; count: number }>
  timeline: Array<{ date: string; tokens: number; cost: number; count: number }>
  agentBreakdown?: Array<{
    agentId: string | null
    _sum: { totalTokens: number | null; promptTokens: number | null; completionTokens: number | null }
    _count: { id: number }
  }>
}

export interface Org {
  id: string
  name: string
  slug: string
  sector: string
  logoUrl: string | null
  plan: 'FREE' | 'PRO' | 'ENTERPRISE'
  workspaceType: WorkspaceType
  customDomain: string | null
  domainStatus: DomainStatus
  themeConfig: Record<string, unknown>
}

export interface Workspace {
  id: string
  name: string
  slug: string
  role: WorkspaceRole
  sector: string
  workspaceType: WorkspaceType
  customDomain: string | null
  domainStatus: DomainStatus
  logoUrl: string | null
  themeConfig: Record<string, unknown>
  parentOrgId: string | null
  createdAt: string
  updatedAt: string
}

export interface WorkspaceListResponse {
  activeWorkspaceId: string | null
  workspaces: Workspace[]
}

export interface CreateWorkspaceInput {
  name: string
  sector?: string
  parentOrgId?: string
}

export interface UpdateWorkspaceInput {
  name?: string
  sector?: string
  logoUrl?: string
  customDomain?: string
  domainStatus?: DomainStatus
  themeConfig?: Record<string, unknown>
}

export interface OnboardingContact {
  pendingEmail: string | null
  newsletterOptIn: boolean
}

export interface OpsSecretRef {
  id: string
  name: string
  provider: string
  isActive: boolean
}

export interface OpsSecret {
  id: string
  name: string
  provider: string
  scope: 'SHARED_OPS' | 'WORKSPACE'
  isActive: boolean
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
  rotatedAt: string | null
  createdById: string | null
  orgId: string
}

export interface CreateOpsSecretInput {
  name: string
  provider: string
  value: string
  scope?: 'SHARED_OPS' | 'WORKSPACE'
  metadata?: Record<string, unknown>
}

export interface UpdateOpsSecretInput {
  name?: string
  value?: string
  isActive?: boolean
  metadata?: Record<string, unknown>
}

export interface HostingConnection {
  id: string
  name: string
  connectorType: ConnectorType
  status: ConnectorStatus
  externalProjectId: string | null
  externalEnvironmentId: string | null
  metadata: Record<string, unknown>
  healthStatus: Record<string, unknown>
  orgId: string
  credentialSecretId: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateHostingConnectionInput {
  name: string
  connectorType: ConnectorType
  credentialSecretId?: string
  externalProjectId?: string
  externalEnvironmentId?: string
  metadata?: Record<string, unknown>
}

export interface UpdateHostingConnectionInput {
  name?: string
  status?: ConnectorStatus
  credentialSecretId?: string
  externalProjectId?: string
  externalEnvironmentId?: string
  metadata?: Record<string, unknown>
}

export interface ChannelProfile {
  id: string
  name: string
  channelType: ChannelType
  config: Record<string, unknown>
  isActive: boolean
  createdAt: string
  updatedAt: string
  orgId: string
  credentialSecretId: string | null
  credentialSecret: OpsSecretRef | null
}

export interface CreateChannelProfileInput {
  name: string
  channelType: 'TELEGRAM' | 'WHATSAPP' | 'PHONE'
  credentialSecretId?: string
  config?: Record<string, unknown>
  isActive?: boolean
}

export interface UpdateChannelProfileInput {
  name?: string
  credentialSecretId?: string
  config?: Record<string, unknown>
  isActive?: boolean
}

export interface ChannelEndpoint {
  id: string
  name: string
  channelType: 'TELEGRAM' | 'WHATSAPP' | 'PHONE'
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ERROR'
  webhookPath: string
  config: Record<string, unknown>
  metadata: Record<string, unknown>
  orgId: string
  channelProfileId: string | null
  channelProfile: {
    id: string
    name: string
    channelType: 'TELEGRAM' | 'WHATSAPP' | 'PHONE'
    isActive: boolean
  } | null
  createdAt: string
  updatedAt: string
}

export interface CreateChannelEndpointInput {
  name: string
  channelType: 'TELEGRAM' | 'WHATSAPP' | 'PHONE'
  channelProfileId?: string
  config?: Record<string, unknown>
  metadata?: Record<string, unknown>
  status?: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ERROR'
}

export interface UpdateChannelEndpointInput {
  name?: string
  config?: Record<string, unknown>
  metadata?: Record<string, unknown>
  status?: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ERROR'
}

export interface AgentChannelBinding {
  id: string
  agentId: string
  channelEndpointId: string
  isDefault: boolean
  routingConfig: Record<string, unknown>
  createdAt: string
  updatedAt: string
  channelEndpoint: ChannelEndpoint
}

export interface CreateAgentChannelBindingInput {
  agentId: string
  endpointId: string
  isDefault?: boolean
  routingConfig?: Record<string, unknown>
}

export interface ConversationThread {
  id: string
  channelType: ChannelType
  externalUserKey: string
  displayName: string | null
  orgId: string
  agentId: string | null
  channelEndpointId: string | null
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface ConversationMessage {
  id: string
  threadId: string
  role: 'USER' | 'ASSISTANT' | 'SYSTEM'
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL'
  content: string
  providerMessageId: string | null
  metadata: Record<string, unknown>
  createdAt: string
}

export interface OfferingPack {
  id: string
  name: string
  description: string | null
  audience: string
  vertical: string
  defaultSystemPrompt: string | null
  defaultChannelConfig: Record<string, unknown>
  locale: string
  isActive: boolean
  orgId: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateOfferingPackInput {
  name: string
  description?: string
  audience: string
  vertical: string
  defaultSystemPrompt?: string
  defaultChannelConfig?: Record<string, unknown>
  locale?: string
  isActive?: boolean
}

export interface InvoiceLineItem {
  id: string
  invoicePeriodId: string
  channelType: ChannelType | null
  providerId: string | null
  agentId: string | null
  usageTokens: number
  requestCount: number
  costUsd: string
  revenueShareUsd: string
  metadata: Record<string, unknown>
  createdAt: string
}

export interface InvoiceSummary {
  id: string
  periodStart: string
  periodEnd: string
  status: 'DRAFT' | 'FINALIZED'
  currency: string
  revenueSharePct: string
  totalTokens: number
  totalRequests: number
  totalCostUsd: string
  totalRevenueShareUsd: string
}

export interface InvoicePeriod extends InvoiceSummary {
  orgId: string
  createdAt: string
  updatedAt: string
  lineItems: InvoiceLineItem[]
}

export interface GenerateInvoiceInput {
  periodStart: string
  periodEnd: string
  revenueSharePct?: number
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}
